import { WeatherUnavailableError, type WeatherGrid, type WeatherUnavailableReason } from "@domain/Weather";
import { apiClient } from "../client";
import { StandardAxiosConfig } from "../common";
import { env } from "../../config/env";

// `WeatherGrid` is a domain model (the pure sampling/averaging ops in `@domain/Weather` operate
// on it). The `/grid` endpoint returns exactly this shape, so the adapter returns it directly.
// Re-exported here for infra-side consumers (e.g. the cache wrapper) that import from this module.
export type { WeatherGrid };

/** One hourly observation. `time` is ms since epoch (UTC). Imperial units throughout (the
 *  backend asks Open-Meteo for °F / in). Any variable can be null when the upstream provider
 *  didn't report it for that hour. */
export interface WeatherPoint {
	time: number;
	temperatureF: number | null;
	precipitationIn: number | null;
}

export interface WeatherRange {
	/** The lat/lon Open-Meteo actually used (snapped to its grid — usually within 0.1°). */
	latitude: number;
	longitude: number;
	points: WeatherPoint[];
}

/** Historical weather from the customer-portal proxy → Open-Meteo Archive API.
 *  See `API/src/routers/weather.py` for the upstream contract and caching strategy. */
export class WeatherAPI {
	private baseUrl: string;

	constructor() {
		this.baseUrl = `${env.rogoApiUrl}/weather`;
	}

	/** Fetch hourly observations for a lat/lon over a UTC date range (inclusive on both ends).
	 *  Returns `[]` if `start` is in the future (Open-Meteo's archive lags ~5 days). */
	public async range(lat: number, lon: number, start: Date, end: Date): Promise<WeatherRange> {
		const url = `${this.baseUrl}/range`;
		const response = await apiClient.get<WeatherRange>(url, {
			...StandardAxiosConfig(),
			params: {
				lat,
				lon,
				start: toISODate(start),
				end: toISODate(end),
			},
		});
		return response.data;
	}

	/** Fetch a daily-high temperature grid over a bounding box, covering the ±5-day window the
	 *  backend's forecast call returns. `cols`/`rows` are the lattice resolution (the backend
	 *  clamps them). See `API/src/routers/weather_grid.py` `get_grid`.
	 *
	 *  Rejects with a {@link WeatherUnavailableError} naming why, and opts out of the app-wide error
	 *  toast and Sentry report: the grid feeds an optional overlay that explains its own failures
	 *  quietly and retries them, and they are upstream conditions the backend keeps out of Sentry. */
	public async grid(
		west: number,
		south: number,
		east: number,
		north: number,
		cols: number,
		rows: number,
	): Promise<WeatherGrid> {
		const url = `${this.baseUrl}/grid`;
		try {
			const response = await apiClient.get<WeatherGrid>(url, {
				...StandardAxiosConfig(),
				params: { west, south, east, north, cols, rows },
				suppressErrorToast: true,
				suppressErrorReport: true,
			});
			return response.data;
		} catch (err: unknown) {
			const status = (err as { response?: { status?: number } }).response?.status;
			throw new WeatherUnavailableError(gridUnavailableReason(status), { cause: err });
		}
	}

	/** Build the URL for a NEXRAD radar frame PNG. The backend snaps `timestampMs` to the
	 *  5-min frame grid and proxies + S3-caches Iowa State Mesonet's CONUS composite.
	 *  Returns just the URL string (no fetch — MapLibre's image source fetches the bytes
	 *  itself, so we get free browser caching off the immutable Cache-Control header). */
	public radarFrameUrl(timestampMs: number): string {
		return `${this.baseUrl}/radar?ts=${timestampMs}`;
	}
}

/** The grid proxy's upstream statuses (`weather_grid.py`): 504 is Open-Meteo stalling past the
 *  deadline and 503 the fail-fast cooldown that follows two such stalls, 429 its rate limit, 502 any
 *  other upstream failure. Anything else — a 500, or no response at all — isn't one the provider caused. */
function gridUnavailableReason(status: number | undefined): WeatherUnavailableReason {
	switch (status) {
		case 503:
		case 504:
			return "timed-out";
		case 429:
			return "rate-limited";
		case 502:
			return "provider-error";
		default:
			return "unexpected";
	}
}

function toISODate(d: Date): string {
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}
